import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { resolveFileDisplayUrl } from '@/lib/files/service';
import { apiGet } from '@/lib/http/client';

import {
  applyResolvedInlineImages,
  extractInlineImageFileIds,
  type InlineImageFileDetail,
} from './inlineImages';

/**
 * Inline-image URLs are stable for a while but not forever — under S3 storage
 * `file_url` is a presigned URL that expires (default 1h). Five minutes keeps a
 * preview reopened a few times off the network without risking a stale link.
 */
const INLINE_IMAGE_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Resolve inline-image references in composed email HTML so they render.
 *
 * Returns `html` with every `<img>` that references an uploaded file pointed at
 * a real URL. Images resolve independently and the HTML is re-derived as each
 * lands, so a slow file doesn't hold up the rest of the preview.
 *
 * ## Why the resolved URL works inside the preview iframe
 *
 * `EmailIframe` renders into a **sandboxed** iframe without `allow-same-origin`,
 * giving the document an opaque origin. The browser therefore withholds our
 * `SameSite=Lax` session cookie from every subresource request it makes, so a
 * cookie-authenticated image URL arrives anonymous, 403s, and is discarded by
 * Chrome's Opaque Response Blocking before the page ever sees it. The URL also
 * has to be absolute, because a srcdoc iframe inherits its base URL from the SPA
 * origin rather than the API's.
 *
 * `file_url` satisfies both: an S3 presigned URL under `USE_S3_STORAGE`, and an
 * absolute signed view URL on local storage. See `apps/files/file_view_url.py`
 * for the full write-up.
 *
 * @param orgId Org whose files to resolve against; pass 0 to disable fetching.
 * @param html  Composed email HTML, typically straight from the preview endpoint.
 */
export function useInlineImageHtml(orgId: number, html: string): string {
  const fileIds = useMemo(() => extractInlineImageFileIds(html), [html]);

  const results = useQueries({
    queries: fileIds.map(fileId => ({
      queryKey: ['orgs', orgId, 'files', fileId] as const,
      queryFn: () =>
        apiGet<InlineImageFileDetail>(`/api/orgs/${orgId}/files/${fileId}/`),
      enabled: orgId > 0,
      staleTime: INLINE_IMAGE_STALE_TIME_MS,
      // A missing or forbidden file will not become available by asking again,
      // and each retry delays the rest of the preview.
      retry: false,
    })),
  });

  // `results` is a fresh array on every render, so it can't be a useMemo dep
  // without recomputing the (DOM-parsing) rewrite each time. Collapse it to a
  // value-based key instead. "\n" is the separator because it cannot appear in
  // a URL, and the id/URL split is on the FIRST ":" since URLs contain their own.
  const resolvedKey = fileIds
    .map((fileId, index) => {
      const data = results[index]?.data;
      const url = data ? resolveFileDisplayUrl(data) : null;
      return `${fileId}:${url ?? ''}`;
    })
    .join('\n');

  return useMemo(() => {
    const urlByFileId: Record<string, string> = {};
    for (const entry of resolvedKey.split('\n')) {
      const separator = entry.indexOf(':');
      if (separator === -1) {
        continue;
      }
      const fileId = entry.slice(0, separator);
      const url = entry.slice(separator + 1);
      if (fileId !== '' && url !== '') {
        urlByFileId[fileId] = url;
      }
    }
    return applyResolvedInlineImages(html, urlByFileId);
  }, [html, resolvedKey]);
}
